import React from 'react';
import { motion } from 'framer-motion';

// Layout components
import FuturisticLayout from '../../components/layout/FuturisticLayout';
import Container from '../../components/layout/Container';

// Futuristic UI components
import { 
  FuturisticButton, 
  FuturisticCard, 
  FuturisticBadge, 
  FuturisticSection, 
  FuturisticHero,
  FuturisticFeatureCard 
} from '../../components/ui/futuristic';

// Mock data
import {
  services,
  caseStudies,
  stats,
  targetAudiences
} from '../../data/mockData';

/**
 * Futuristic Home page component
 */
const FuturisticHomePage: React.FC = () => {
  // Icons for feature cards
  const BrainIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
    </svg>
  );

  const RocketIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  );

  const CodeIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
    </svg>
  );

  const ChartIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  );

  const PipelineIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
    </svg>
  );

  const InboxIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
    </svg>
  );

  const FollowUpIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
    </svg>
  );

  const SettingsIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );

  const WebDesignIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a2 2 0 012-2h12a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 7a2 2 0 104 0 2 2 0 00-4 0z" />
    </svg>
  );

  const SearchIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  );

  const MarketingIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
    </svg>
  );

  // Hero illustration
  const HeroIllustration = () => (
    <div className="w-full max-w-lg">
      <svg className="w-full h-auto" viewBox="0 0 500 400" fill="none" xmlns="http://www.w3.org/2000/svg">
        {/* Brain network visualization */}
        <circle cx="250" cy="200" r="120" fill="rgba(67, 97, 238, 0.1)" stroke="#4361EE" strokeWidth="3" />
        <circle cx="250" cy="200" r="80" fill="transparent" stroke="#BC00DD" strokeWidth="2" strokeDasharray="10 5" />
        
        {/* Nodes */}
        <circle cx="250" cy="80" r="12" fill="#00F5D4" />
        <circle cx="370" cy="200" r="12" fill="#4361EE" />
        <circle cx="250" cy="320" r="12" fill="#BC00DD" />
        <circle cx="130" cy="200" r="12" fill="#F72585" />
        
        <circle cx="310" cy="140" r="8" fill="#3A0CA3" />
        <circle cx="310" cy="260" r="8" fill="#06D6A0" />
        <circle cx="190" cy="260" r="8" fill="#FFBE0B" />
        <circle cx="190" cy="140" r="8" fill="#4CC9F0" />
        
        {/* Connections */}
        <line x1="250" y1="80" x2="310" y2="140" stroke="#00F5D4" strokeWidth="2" />
        <line x1="310" y1="140" x2="370" y2="200" stroke="#4361EE" strokeWidth="2" />
        <line x1="370" y1="200" x2="310" y2="260" stroke="#4361EE" strokeWidth="2" />
        <line x1="310" y1="260" x2="250" y2="320" stroke="#BC00DD" strokeWidth="2" />
        <line x1="250" y1="320" x2="190" y2="260" stroke="#BC00DD" strokeWidth="2" />
        <line x1="190" y1="260" x2="130" y2="200" stroke="#F72585" strokeWidth="2" />
        <line x1="130" y1="200" x2="190" y2="140" stroke="#F72585" strokeWidth="2" />
        <line x1="190" y1="140" x2="250" y2="80" stroke="#00F5D4" strokeWidth="2" />
        
        <line x1="250" y1="80" x2="370" y2="200" stroke="#4361EE" strokeWidth="1" strokeOpacity="0.5" />
        <line x1="370" y1="200" x2="250" y2="320" stroke="#BC00DD" strokeWidth="1" strokeOpacity="0.5" />
        <line x1="250" y1="320" x2="130" y2="200" stroke="#F72585" strokeWidth="1" strokeOpacity="0.5" />
        <line x1="130" y1="200" x2="250" y2="80" stroke="#00F5D4" strokeWidth="1" strokeOpacity="0.5" />
        
        {/* Pulsing center */}
        <circle cx="250" cy="200" r="20" fill="#4361EE" className="animate-pulse-slow" />
        <circle cx="250" cy="200" r="10" fill="#00F5D4" />
      </svg>
    </div>
  );

  return (
    <FuturisticLayout>
      {/* Hero section */}
      <FuturisticHero
        title={
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 leading-tight tracking-tight text-neu-text">
            Your brand,{' '}
            <span className="text-gradient-neu">beautifully built.</span>
            <br />
            Your business,{' '}
            <span className="text-neu-accent">brilliantly automated.</span>
          </h1>
        }
        subtitle="Empowering businesses and individuals with cutting-edge AI — through expert training, stunning web design, seamless automation, and growth systems built to scale."
        primaryButtonText="Book Free Consultation"
        secondaryButtonText="Explore Services"
        onPrimaryClick={() => window.location.href = '/book'}
        onSecondaryClick={() => window.location.href = '/services'}
        illustration={<HeroIllustration />}
        animatedBg={true}
        variant="primary"
      />

      {/* Services section */}
      <FuturisticSection variant="light" pattern="dots">
        <Container>
          <div className="text-center mb-10">
            <FuturisticBadge variant="primary" size="md" className="mb-4">Our Services</FuturisticBadge>
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-extrabold mb-4 tracking-tight text-neu-text">Comprehensive AI Solutions</h2>
            <p className="text-lg text-neu-muted max-w-3xl mx-auto">
              Tailored approaches to help your business harness the full potential of artificial intelligence
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <FuturisticFeatureCard
              title="AI Training & Education"
              description="Comprehensive training programs to help your team master AI tools and technologies for maximum productivity."
              icon={<BrainIcon />}
              color="primary"
            />
            <FuturisticFeatureCard
              title="Business AI Onboarding"
              description="Seamless integration of AI solutions into your existing business processes with minimal disruption."
              icon={<RocketIcon />}
              color="secondary"
            />
            <FuturisticFeatureCard
              title="Workflow Automation"
              description="Streamline repetitive tasks and processes with intelligent automation solutions that save time and reduce errors."
              icon={<SettingsIcon />}
              color="accent"
            />
            <FuturisticFeatureCard
              title="CRM & Pipeline Automation"
              description="Connect your CRM, automate lead intake and follow-up, and build revenue systems that work around the clock."
              icon={<PipelineIcon />}
              color="neon-cyan"
            />
            <FuturisticFeatureCard
              title="Custom AI Development"
              description="Bespoke AI solutions designed specifically for your unique business challenges and opportunities."
              icon={<CodeIcon />}
              color="neon-purple"
            />
            <FuturisticFeatureCard
              title="AI System Optimization"
              description="Fine-tune your existing AI systems for improved performance, accuracy, and efficiency."
              icon={<ChartIcon />}
              color="neon-pink"
            />
            <FuturisticFeatureCard
              title="Web & Software Development"
              description="AI-enhanced websites, web applications, and custom software solutions built to scale with your business and convert visitors into customers."
              icon={<CodeIcon />}
              color="primary"
            />
            <FuturisticFeatureCard
              title="SEO Services"
              description="AI-driven search engine optimization that improves rankings, drives organic traffic, and increases conversions."
              icon={<SearchIcon />}
              color="secondary"
            />
            <FuturisticFeatureCard
              title="Digital Marketing"
              description="Data-driven marketing campaigns with AI insights for maximum ROI and sustainable business growth."
              icon={<MarketingIcon />}
              color="neon-purple"
            />
          </div>
        </Container>
      </FuturisticSection>

      {/* Stats section */}
      <FuturisticSection variant="primary" pattern="grid" glow={true}>
        <Container>
          <div
            className="grid grid-cols-2 md:grid-cols-5 gap-6 bg-neu-bg rounded-neu p-8 md:p-12"
            style={{ boxShadow: 'inset 10px 10px 20px rgb(163,177,198,0.7), inset -10px -10px 20px rgba(255,255,255,0.6)' }}
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`relative text-center${index === stats.length - 1 ? ' col-span-2 md:col-span-1' : ''}`}
              >
                <p className="font-display text-3xl md:text-4xl font-extrabold mb-2 text-gradient-neu">{stat.value}</p>
                <p className="text-neu-muted text-sm md:text-base font-display">
                  {stat.label}
                  {stat.unit && <span className="block text-xs mt-1 text-neu-muted/70">{stat.unit}</span>}
                </p>
              </motion.div>
            ))}
          </div>
        </Container>
      </FuturisticSection>

      {/* B2B Growth Systems section */}
      <FuturisticSection variant="light" pattern="grid">
        <Container>
          <div className="text-center mb-10">
            <FuturisticBadge variant="accent" size="md" className="mb-4">Growth Systems</FuturisticBadge>
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-extrabold mb-4 tracking-tight text-neu-text">
              Built for Businesses & Individuals
            </h2>
            <p className="text-lg text-neu-muted max-w-3xl mx-auto">
              Whether you're a solo creator, a growing brand, or a B2B service firm — we build AI-powered systems that attract, convert, and retain customers automatically.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10">
            {[
              {
                icon: <PipelineIcon />,
                color: 'primary' as const,
                title: 'Your Brand, Beautifully Built',
                description: 'From stunning websites to custom software — we design and build digital experiences that make your brand stand out and convert visitors into customers.',
              },
              {
                icon: <InboxIcon />,
                color: 'secondary' as const,
                title: 'Automate the Repetitive',
                description: 'AI-powered workflows handle your lead intake, CRM updates, follow-ups, and marketing — so you focus on what only you can do.',
              },
              {
                icon: <FollowUpIcon />,
                color: 'accent' as const,
                title: 'Grow Without the Overhead',
                description: 'Whether you\'re a solo creator or a scaling business, our systems attract, nurture, and convert customers around the clock — no extra headcount needed.',
              },
            ].map((item, index) => (
              <FuturisticFeatureCard
                key={index}
                title={item.title}
                description={item.description}
                icon={item.icon}
                color={item.color}
              />
            ))}
          </div>

          <div className="text-center">
            <p className="text-neu-muted font-display font-semibold mb-6 text-lg">For brands, businesses, and individuals alike.</p>
            <FuturisticButton
              variant="accent"
              size="lg"
              glow
              onClick={() => window.location.href = '/services'}
            >
              Explore All Services
            </FuturisticButton>
          </div>
        </Container>
      </FuturisticSection>

      {/* Target audiences section */}
      <FuturisticSection variant="dark" pattern="dots">
        <Container>
          <div className="text-center mb-10">
            <FuturisticBadge variant="accent" size="md" className="mb-4">For Everyone</FuturisticBadge>
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-extrabold mb-4 tracking-tight text-neu-text">AI Solutions That Scale</h2>
            <p className="text-lg text-neu-muted max-w-3xl mx-auto">
              From solo professionals to enterprise organizations, our AI solutions adapt to your specific needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
            {targetAudiences.slice(0, 2).map((audience, index) => (
              <div key={audience.id} className="h-full">
                <FuturisticCard hoverEffect={true} glowColor={index === 0 ? 'secondary' : 'accent'} className="h-full flex flex-col">
                  <div className="flex items-start mb-6">
                    <div
                      className="w-12 h-12 flex items-center justify-center rounded-full flex-shrink-0"
                      style={{
                        background: '#E0E5EC',
                        color: index === 0 ? '#38B2AC' : '#6C63FF',
                        boxShadow: 'inset 6px 6px 10px rgb(163,177,198,0.6), inset -6px -6px 10px rgba(255,255,255,0.5)',
                      }}
                    >
                      {index === 0 ? <BrainIcon /> : <RocketIcon />}
                    </div>
                    <h3 className="font-display text-xl font-bold ml-4 mt-2 text-neu-text">{audience.title}</h3>
                  </div>

                  <p className="text-neu-muted mb-6">{audience.description}</p>

                  <ul className="space-y-3 mb-6 flex-grow">
                    {audience.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <svg
                          className="w-5 h-5 mt-0.5 mr-2 flex-shrink-0"
                          style={{ color: index === 0 ? '#38B2AC' : '#6C63FF' }}
                          fill="none" viewBox="0 0 24 24" stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-neu-muted">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <FuturisticButton
                    variant={index === 0 ? 'secondary' : 'accent'}
                    onClick={() => window.location.href = audience.ctaLink}
                    className="w-full mt-auto"
                  >
                    {audience.ctaText}
                  </FuturisticButton>
                </FuturisticCard>
              </div>
            ))}
          </div>
        </Container>
      </FuturisticSection>

      {/* Call to action */}
      <FuturisticSection variant="dark" pattern="grid" glow={true} waveTop={true} waveBottom={true}>
        <Container>
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-extrabold mb-6 tracking-tight text-gradient-neu">
              Ready to Transform Your Business with AI?
            </h2>
            <p className="text-lg text-neu-muted mb-8 max-w-3xl mx-auto">
              Get started with a free consultation to discuss how our AI solutions can address your specific business challenges.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <FuturisticButton 
                variant="accent" 
                size="lg" 
                glow={true}
                animated={true}
                onClick={() => window.location.href = '/contact'}
              >
                Schedule Free Consultation
              </FuturisticButton>
              <FuturisticButton 
                variant="outline" 
                size="lg"
                glow={true}
                onClick={() => window.location.href = '/about'}
              >
                Learn About Our Approach
              </FuturisticButton>
            </div>
            
            {/* Decorative neumorphic dots */}
            <div className="mt-12 flex justify-center space-x-3">
              <div className="w-3 h-3 rounded-full bg-neu-accent animate-pulse-slow shadow-[0_2px_8px_rgba(108,99,255,0.5)]"></div>
              <div className="w-3 h-3 rounded-full bg-neu-teal animate-pulse-slow shadow-[0_2px_8px_rgba(56,178,172,0.5)]" style={{ animationDelay: '0.3s' }}></div>
              <div className="w-3 h-3 rounded-full bg-neu-accent-light animate-pulse-slow shadow-[0_2px_8px_rgba(139,132,255,0.5)]" style={{ animationDelay: '0.6s' }}></div>
            </div>
          </div>
        </Container>
      </FuturisticSection>
    </FuturisticLayout>
  );
};

export default FuturisticHomePage;